import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ClipboardList, Flag, FolderOpen, Globe, Headset, Inbox, LayoutDashboard, LogOut, Mail, MessagesSquare, PencilRuler, Radio, Send, Settings, ShieldAlert, ShieldCheck, UserRound, Users, FolderPlus, FolderKanban, Landmark } from "lucide-react";
import { useAdmin } from "@/context/AdminContext";
import { api } from "@/lib/api";
import { PortalFiles, FolderRequest } from "@/components/portal/PortalFiles";
import { MyRequests, Profile, Avatar } from "@/components/portal/StaffSelf";
import { RequestsInbox, StaffManager } from "@/components/portal/OwnerTools";
import FilesPage from "@/pages/FilesPage";
import ContentFilesPage from "@/pages/ContentFilesPage";
import SubscribersPage from "@/pages/SubscribersPage";
import SendEmailPage from "@/pages/SendEmailPage";
import MaintenancePage from "@/pages/MaintenancePage";
import { SubmissionsHub } from "@/components/submissions/SubmissionsHub";
import { CasesHub } from "@/components/cases/CasesHub";
import { ChannelStudio } from "@/components/channel/ChannelStudio";
import { ChannelCommentReports } from "@/components/channel/ChannelCommentReports";
import { ChannelSupportInbox } from "@/components/channel/ChannelSupportInbox";
import { ChannelAccountAdmin } from "@/components/channel/ChannelAccountAdmin";
import { FanMailInbox } from "@/components/site/FanMailInbox";
import { BankDetailsAdmin } from "@/pages/BankDetailsPage";

const Stat = ({ label, value, testId, accent }) => (
  <div className="border border-black/15 bg-white p-5" data-testid={testId}>
    <p className="text-xs font-bold uppercase tracking-wider text-[#57534E]">{label}</p>
    <p className={`mt-2 font-display text-4xl font-extrabold ${accent || ""}`}>{value}</p>
  </div>
);

const LinkCard = ({ onClick, title, text, Icon, testId }) => (
  <button onClick={onClick} data-testid={testId} className="group flex items-start gap-4 border border-black/15 bg-white p-5 text-left transition-colors hover:border-[#0A0A0A]">
    <Icon className="h-6 w-6 shrink-0 text-[#E62314]" /><span><span className="block font-bold group-hover:text-[#E62314]">{title}</span><span className="text-sm text-[#57534E]">{text}</span></span>
  </button>
);

export default function Dashboard() {
  const { user, isAdmin, isStaff, can, canEdit, logout, setSiteMode } = useAdmin();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [requests, setRequests] = useState([]);
  const [mine, setMine] = useState([]);
  const [offers, setOffers] = useState(0);
  const [reports, setReports] = useState(0);
  const [secReports, setSecReports] = useState(0);
  const [fanMailUnread, setFanMailUnread] = useState(0);
  const [commentReportsPending, setCommentReportsPending] = useState(0);
  const [channelCustomerId, setChannelCustomerId] = useState("");
  const returnFromChannelAccount = useCallback(() => setChannelCustomerId(""), []);
  const [staff, setStaff] = useState([]);
  const [reqOpen, setReqOpen] = useState(false);

  const load = useCallback(() => {
    if (can("approve_requests")) api.get("/requests").then((r) => setRequests(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    if (!isAdmin && can("my_requests")) api.get("/requests/mine").then((r) => setMine(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    if (can("staff_management")) api.get("/staff").then((r) => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    if (can("submissions")) api.get("/queues/offers").then((r) => setOffers(Array.isArray(r.data) ? r.data.length : 0)).catch(() => {});
    if (can("submissions")) api.get("/fan-mail/inbox").then((r) => setFanMailUnread(Array.isArray(r.data) ? r.data.filter((item) => item.unread).length : 0)).catch(() => {});
    if (can("channel_comment_reports")) api.get("/channel/customer/admin/comment-reports").then((r) => setCommentReportsPending(Array.isArray(r.data) ? r.data.filter((item) => item.status === "pending").length : 0)).catch(() => {});
    const waiting = (r) => (Array.isArray(r.data) ? r.data.filter((x) => x?.status === "waiting").length : 0);
    if (isAdmin) api.get("/cases/reports", { params: { area: "complaints" } }).then((r) => setReports(waiting(r))).catch(() => {});
    if (isAdmin) api.get("/cases/reports", { params: { area: "security" } }).then((r) => setSecReports(waiting(r))).catch(() => {});
  }, [can, isAdmin]);
  useEffect(() => { if (user) load(); return undefined; }, [user, load, tab]);
  useEffect(() => {
    if (!user) return undefined;
    const t = setInterval(load, 60000);
    window.addEventListener("cm-offers", load);
    window.addEventListener("cm-reports", load);
    return () => { clearInterval(t); window.removeEventListener("cm-offers", load); window.removeEventListener("cm-reports", load); };
  }, [user, load]);

  if (user === null) return <div className="min-h-screen bg-[#FBF9F5]" />;
  if (!user) return <Navigate to="/login" replace />;

  const pending = requests.filter((r) => r.status === "pending").length;
  const team = staff.filter((u) => u.role !== "admin");
  const nav = [
    ["overview", "Overview", LayoutDashboard, true],
    ["requests", "Requests", Inbox, can("approve_requests"), pending],
    ["my_requests", "My Requests", ClipboardList, !isAdmin && can("my_requests")],
    ["files", "Files & Folders", FolderOpen, can("files")],
    ["submissions", "Submissions", MessagesSquare, can("submissions"), offers, "waiting for you to accept"],
    ["fan_mail", "Fan Mail", Mail, can("submissions"), fanMailUnread, "unread messages"],
    ["cases", "Complaint Cases", ShieldAlert, can("cases"), reports, "waiting to be opened"],
    ["security", "Account Security", ShieldCheck, can("account_security"), secReports, "waiting to be opened"],
    ["submit", "Submit Requests", Send, !isAdmin && can("submit_requests")],
    ["staff", "Account Management", Users, can("staff_management")],
    ["profile", "My Profile", UserRound, can("profile")],
    ["mailing", "Mailing List", Mail, can("mailing")],
    ["channel", "Channel Studio", Radio, can("channel_studio")],
    ["channel_comment_reports", "Comment Reports", Flag, can("channel_comment_reports"), commentReportsPending, "pending reports"],
    ["channel_accounts", "Customer Accounts", Users, can("channel_accounts")],
    ["channel_support", "Channel Support", Headset, can("channel_support") || canEdit],
    ["content_files", "Content Files", FolderKanban, can("content_files") || canEdit],
    ["send", "Send Email", Send, can("send_email")],
    ["bank_details", "Bank Details", Landmark, isAdmin],
    ["settings", "System Settings", Settings, can("system_settings")],
  ].filter((n) => n[3]);
  const editSite = () => { setSiteMode(true); navigate("/"); };
  const title = nav.find((n) => n[0] === tab)?.[1] || "Overview";

  return (
    <div data-testid="dashboard" className="min-h-screen bg-[#FBF9F5] lg:flex">
      <aside className="bg-[#0A0A0A] text-white lg:sticky lg:top-0 lg:h-screen lg:w-72 lg:shrink-0 lg:overflow-y-auto">
        <div className="flex items-center gap-3 px-5 py-5">
          <img src="/assets/logo-dark.png" alt="Cresway Media logo" className="h-10 w-10 object-contain" />
          <span className="font-display text-lg font-extrabold">Dashboard</span>
        </div>
        <div className="mx-5 mb-4 flex items-center gap-3 border-y border-white/10 py-4" data-testid="dashboard-user">
          <Avatar user={user} size="h-11 w-11" />
          <div className="min-w-0"><p className="break-words text-sm font-bold" data-testid="dashboard-user-name">{user.name || ""}</p>{user.job_title && <p className="break-words text-xs text-zinc-400" data-testid="dashboard-user-role">{user.job_title}</p>}</div>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col lg:overflow-visible" data-testid="dashboard-nav">
          {nav.map(([key, label, Icon, , badge, red]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`dashboard-tab-${key}`}
              className={`flex shrink-0 items-center gap-3 whitespace-nowrap rounded px-3 py-2.5 text-sm font-semibold transition-colors ${tab === key ? "bg-[#E62314]" : "hover:bg-white/10"}`}>
              <Icon className="h-4 w-4" />{label}{badge ? <span title={red ? `${badge} ${red}` : undefined} className={`ml-auto rounded-full px-2 text-xs font-bold ${red && tab !== key ? "bg-[#E62314] text-white" : "bg-white text-[#E62314]"}`} data-testid={`dashboard-badge-${key}`}>{badge}</span> : null}
            </button>
          ))}
          <button onClick={() => { setSiteMode(false); navigate("/"); }} data-testid="dashboard-view-website" className="flex shrink-0 items-center gap-3 whitespace-nowrap rounded px-3 py-2.5 text-sm font-semibold hover:bg-white/10"><Globe className="h-4 w-4" />View Website</button>
          {canEdit && <button onClick={editSite} data-testid="dashboard-edit-website" className="flex shrink-0 items-center gap-3 whitespace-nowrap rounded px-3 py-2.5 text-sm font-semibold hover:bg-white/10"><PencilRuler className="h-4 w-4" />Edit Website</button>}
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="dashboard-logout" className="flex shrink-0 items-center gap-3 whitespace-nowrap rounded px-3 py-2.5 text-sm font-semibold text-zinc-300 hover:bg-white/10 lg:mt-4"><LogOut className="h-4 w-4" />Log out</button>
        </nav>
      </aside>
      <main className="min-w-0 flex-1 px-5 pb-28 pt-8 sm:px-8 lg:px-12 lg:pt-12">
        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="dashboard-title">{tab === "overview" ? `Hi ${(user.name || "there").split(" ")[0]}` : title}</h1>
        <div className="mt-8">
          {tab === "overview" && (
            <div className="space-y-8" data-testid="dashboard-overview">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {isAdmin ? <>
                  <Stat label="Waiting for you" value={pending} accent={pending ? "text-[#E62314]" : ""} testId="stat-pending" />
                  <Stat label="Active staff" value={team.filter((u) => u.status === "active").length} testId="stat-active" />
                  <Stat label="Invites sent" value={team.filter((u) => u.status === "invited").length} testId="stat-invited" />
                  <Stat label="Suspended" value={team.filter((u) => ["locked", "suspended"].includes(u.status)).length} testId="stat-suspended" />
                </> : <>
                  <Stat label="Pending" value={mine.filter((r) => r.status === "pending").length} testId="stat-my-pending" />
                  <Stat label="Approved" value={mine.filter((r) => r.status === "approved").length} testId="stat-my-approved" />
                  <Stat label="Declined" value={mine.filter((r) => r.status === "declined").length} testId="stat-my-declined" />
                </>}
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                {canEdit && <button onClick={editSite} className="flex items-start gap-4 border border-black/15 bg-white p-5 text-left hover:border-[#0A0A0A]" data-testid="overview-edit-website"><PencilRuler className="h-6 w-6 text-[#E62314]" /><span><span className="block font-bold">Edit Website</span><span className="text-sm text-[#57534E]">Change text, pages and photos on the live site</span></span></button>}
                {isAdmin && <LinkCard onClick={() => setTab("files")} title="Files & Folders" text="Manage every folder, file and permission level" Icon={FolderOpen} testId="overview-files" />}
                {(isAdmin || canEdit) && <LinkCard onClick={() => setTab("content_files")} title="Content Files" text="Organise videos and images for your content" Icon={FolderKanban} testId="overview-file-store" />}
                {isAdmin && <LinkCard onClick={() => setTab("settings")} title="System Settings" text="Coming Soon, offline mode, schedules and reports" Icon={Settings} testId="overview-maintenance" />}
              </div>
            </div>
          )}
          {tab === "requests" && <RequestsInbox requests={requests} onChanged={load} />}
          {tab === "my_requests" && <MyRequests list={mine} />}
          {tab === "files" && (isAdmin ? <FilesPage embedded /> : <PortalFiles onRequested={load} />)}
          {tab === "content_files" && <ContentFilesPage embedded />}
          {tab === "submissions" && <SubmissionsHub />}
          {tab === "fan_mail" && <FanMailInbox />}
          {tab === "cases" && <CasesHub key="complaints" area="complaints" />}
          {tab === "security" && <CasesHub key="security" area="security" />}
          {tab === "submit" && (can("files") ? <PortalFiles onRequested={load} /> : (
            <button onClick={() => setReqOpen(true)} className="flex items-center gap-2 bg-[#0A0A0A] px-5 py-3 text-sm font-bold text-white" data-testid="submit-request-folder"><FolderPlus className="h-4 w-4" />Request New Folder</button>
          ))}
          {tab === "staff" && <StaffManager staff={staff} onChanged={load} />}
          {tab === "profile" && <Profile />}
          {tab === "mailing" && <div className="-mx-5 sm:-mx-8 lg:-mx-12"><SubscribersPage /></div>}
          {tab === "channel" && <ChannelStudio />}
          {tab === "channel_comment_reports" && can("channel_comment_reports") && <ChannelCommentReports onOpenAccount={(id) => { setChannelCustomerId(id); setTab("channel_accounts"); }} onChanged={load} />}
          {tab === "channel_accounts" && <ChannelAccountAdmin customerId={channelCustomerId} onBack={returnFromChannelAccount} />}
          {tab === "channel_support" && <ChannelSupportInbox />}
          {tab === "send" && <div className="-mx-5 sm:-mx-8 lg:-mx-12"><SendEmailPage /></div>}
          {tab === "bank_details" && isAdmin && <BankDetailsAdmin />}
          {tab === "settings" && <div className="-mx-5 sm:-mx-8 lg:-mx-12"><MaintenancePage /></div>}
        </div>
      </main>
      <FolderRequest open={reqOpen} onOpenChange={setReqOpen} parent={null} onDone={load} />
    </div>
  );
}
